import { Checkbox, TextField, Typography } from '@gupshup-frontend/gupshup-ts-components';
import { isAuthentication, isPasskey } from '../state';
import { checkExpirationTimeFormat, clampExpirationTime } from '../validate';
import { friendlyError } from './errorMessages';

// Console's own input cap (task-specified) — expirationTime has no reducer-carried constant of
// its own to carry one alongside.
const EXPIRATION_MINUTES_MAX_CHARS = 2;

// Auth message content — Authentication-only card (not shown for Passkey, mirrors the console's
// own gate: gs-ui-templates Auth/MessageContent.tsx:154, `if (!isAuthentication() || isPasskey())
// return null`) for the security-recommendation and expiration-time checkboxes. payload.js
// carries the matching defensive guard on the two fields these checkboxes drive, in case a flag
// set before switching to Passkey survives underneath (messageContent isn't reset on a
// subcategory change).
const AuthMessageContentSection = ({ state, dispatch, errors, disabled }) => {
  if (!isAuthentication(state) || isPasskey(state)) return null;

  // Live per-keystroke error (set by the expirationTime TextField's own onChange below) wins over
  // the submit-time aggregate — same precedence Footer.js already uses for footer.footerText.
  const expirationTimeDisplayError = state.messageContent.expirationTime.error
    || errors['messageContent.expirationTime.value'];

  return (
    <div className="br-tpl-form-card" data-testid="bloomreach-templates-form-auth-message-content">
      <div className="br-tpl-form-card-heading-row">
        <div className="br-tpl-form-card-heading">
          <Typography variant="subtitle1" data-testid="bloomreach-templates-form-auth-message-content-heading">
            Message Content
          </Typography>
        </div>
      </div>
      <Typography variant="caption" className="br-tpl-form-helper-text">
        Content for authentication templates can’t be edited. You can add additional content from the options below
      </Typography>

      <Checkbox
        label="Add Security recommendation"
        checked={state.messageContent.isSecurityCheck}
        onChange={(event) => dispatch({
          type: 'set',
          path: 'messageContent.isSecurityCheck',
          value: event.target.checked,
        })}
        disabled={disabled}
        data-testid="bloomreach-templates-form-auth-security-checkbox"
      />

      <Checkbox
        label="Add expiration time for code"
        checked={state.messageContent.isExpirationTimeChecked}
        onChange={(event) => {
          const { checked } = event.target;
          dispatch({
            type: 'merge',
            path: 'messageContent',
            value: {
              isExpirationTimeChecked: checked,
              // B2: ticking also seeds the value to 1, mirroring the console's own
              // handleExpirationChecked (Auth/MessageContent.tsx:136-145). Unticking leaves the
              // existing value alone — omitted from this merge rather than reset.
              ...(checked ? { expirationTime: { value: '1', error: '' } } : {}),
            },
          });
        }}
        disabled={disabled}
        data-testid="bloomreach-templates-form-auth-expiration-checkbox"
      />
      <Typography variant="caption" className="br-tpl-form-helper-text">
        After the code expires, Autofill button will be disabled.
      </Typography>
      {state.messageContent.isExpirationTimeChecked && (
        <TextField
          fullWidth
          type="text"
          label="Minutes Value"
          value={state.messageContent.expirationTime.value}
          onChange={(event) => dispatch({
            type: 'merge',
            path: 'messageContent.expirationTime',
            value: { value: event.target.value, error: checkExpirationTimeFormat(event.target.value) },
          })}
          onBlur={() => {
            // clampExpirationTime is a value CORRECTION, not an error — the console's own
            // behavior is to silently clamp an out-of-range value into 1-90 rather than reject
            // it. Applied on blur (not onChange) so a mid-typed "9" isn't clamped before the
            // user finishes typing "90".
            const { value } = state.messageContent.expirationTime;
            const clamped = clampExpirationTime(value);
            if (clamped !== value) {
              dispatch({
                type: 'merge',
                path: 'messageContent.expirationTime',
                value: { value: clamped, error: checkExpirationTimeFormat(clamped) },
              });
            }
          }}
          disabled={disabled}
          placeholder="Minutes Value"
          error={Boolean(expirationTimeDisplayError)}
          helperText={friendlyError(expirationTimeDisplayError)}
          autoComplete="off"
          inputProps={{
            // B2: type="number" made browsers ignore maxLength — a text input with an explicit
            // numeric inputMode keeps the numeric keyboard on mobile while maxLength actually works.
            inputMode: 'numeric',
            maxLength: EXPIRATION_MINUTES_MAX_CHARS,
            'data-testid': 'bloomreach-templates-form-auth-expiration-textfield',
          }}
        />
      )}
    </div>
  );
};

export default AuthMessageContentSection;
